/* DeleteAction.js */

"use strict";

const PLATFORM_NAME_MAX_LENGTH = 60;
const PLATFORM_NAME_LIST_LENGTH = 10;
const WARNING_LINKED_DEVICES =
    "Sorry! IoT Platform linked to one or more IoT Devices cannot be deleted.\n\n";

class DeleteAction {
    constructor(logger, gameEditor, modalUserFeedback, userSession, OdmOsPlatformEntity) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = modalUserFeedback;
        this._activeView = this._gameEditor._activeView;
        this._activeOrgId = userSession.activeOrganization.id;
        this._OdmOsPlatformEntity = OdmOsPlatformEntity;
        this._selectedPlatformIds = [];
        this._selectedPlatformNames = [];

        if (this._activeView && this._activeView.pagingFeed) {
            this._selectedPlatformIds =
                this._activeView.getSelectedOdmOsPlatformIds().selectedRows || [];
        }

        this.run = this.run.bind(this);
        this._deletePlatforms = this._deletePlatforms.bind(this);
    }

    run() {
        if (this._selectedPlatformIds.length) {
            this._activeView.pagingFeed
                .waitForRangeIdsUpdated()
                .then(async () => {
                    const query = {
                        id: {$in: this._selectedPlatformIds},
                    };

                    const platforms = await this._OdmOsPlatformEntity.loadOdmOsPlatforms(
                        this._activeOrgId,
                        query,
                        {
                            id: true,
                            name: true,
                            deviceCount: true,
                        }
                    );

                    // Separate platforms into deletable (deviceCount == 0) and non-deletable (deviceCount > 0)
                    const deletablePlatforms = [];
                    const nonDeletablePlatforms = [];

                    platforms.forEach((platform) => {
                        if (platform.deviceCount > 0) {
                            nonDeletablePlatforms.push(platform);
                        } else {
                            deletablePlatforms.push(platform);
                        }
                    });

                    this._deletablePlatformIds = deletablePlatforms.map((p) => p.id);
                    this._deletablePlatformNames = deletablePlatforms.map((p) => p.name);

                    let message = "";
                    let confirmDetails = {
                        text: "Confirm",
                        type: "destructive",
                    };
                    let rejectDetails = {
                        text: "Cancel",
                        type: "text",
                    };

                    // Case 1: All selected platforms have deviceCount > 0 (none deletable)
                    if (deletablePlatforms.length === 0) {
                        message = WARNING_LINKED_DEVICES;
                        confirmDetails = {
                            text: "OK",
                            type: "destructive",
                            classes: "hidden",
                        };
                        rejectDetails = {
                            text: "OK",
                            type: "text",
                        };
                    }
                    // Case 2: All selected platforms have deviceCount == 0 (all deletable)
                    else if (nonDeletablePlatforms.length === 0) {
                        const platformCount = deletablePlatforms.length;
                        const s = platformCount > 1 ? "s" : "";

                        if (platformCount <= PLATFORM_NAME_LIST_LENGTH) {
                            message = `Are you sure you want to delete the platform${s}?\n`;
                            message += this._getPlatformList(this._deletablePlatformNames);
                        } else {
                            message = `Are you sure you want to delete the ${platformCount} IoT platforms?\n`;
                        }
                    }
                    // Case 3: Mixed - some deletable, some not
                    else {
                        message = WARNING_LINKED_DEVICES;

                        const platformCount = deletablePlatforms.length;
                        const s = platformCount > 1 ? "s" : "";

                        if (platformCount <= PLATFORM_NAME_LIST_LENGTH) {
                            message += `Are you sure you want to delete the platform${s}?\n`;
                            message += this._getPlatformList(this._deletablePlatformNames);
                        } else {
                            message += `Are you sure you want to delete the ${platformCount} platforms?\n`;
                        }
                    }

                    return this._gameEditor
                        .runQuickConfirmationActionWorkflow(
                            {
                                message: message,
                                title: "Delete",
                                confirmDetails,
                                rejectDetails,
                            },
                            this._deletePlatforms
                        )
                        .catch((err) => {
                            if (err.userRejected) {
                                return this._modalUserFeedback.hideDisplayed();
                            }
                            this._logger.warn({error: err}, "DeleteAction.run() - Error");
                            return this._modalUserFeedback.displayError({error: {errorMsg: err}});
                        });
                })
                .catch((err) => {
                    this._logger.warn(
                        {error: err},
                        "Something went wrong while retrieving platforms - Error"
                    );
                });
        } else {
            return Promise.resolve();
        }
    }

    _deletePlatforms() {
        // Only delete platforms that are deletable (deviceCount == 0)
        if (!this._deletablePlatformIds || this._deletablePlatformIds.length === 0) {
            return Promise.resolve();
        }

        const workTarget = {
            method: "multi",
            ids: this._deletablePlatformIds,
        };
        const showProgFunc = (prog) => {
            this._modalUserFeedback.showLoadingProgress(
                `${prog.successCount} of ${prog.targetCount}`
            );
        };

        return this._OdmOsPlatformEntity
            .runDeleteOdmOsPlatformsWork(this._activeOrgId, workTarget, undefined, showProgFunc)
            .then((workResults) => {
                this._OdmOsPlatformEntity.throwWorkErrors(workResults);
                this._activeView.resetSelection();
                if (
                    this._activeView &&
                    this._activeView.pagingFeed &&
                    this._activeView.pagingFeed.feedUpdate
                ) {
                    this._activeView.pagingFeed.feedUpdate();
                }
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn({error: err}, "DeleteAction._deletePlatforms() - Error");
                throw err;
            });
    }

    _getPlatformList(platformNames) {
        let platformList = "";
        const platforms = platformNames.slice(0, PLATFORM_NAME_LIST_LENGTH);
        platforms.forEach((platformName) => {
            let shortPlatformName =
                platformName.length > PLATFORM_NAME_MAX_LENGTH
                    ? platformName.substring(0, PLATFORM_NAME_MAX_LENGTH) + "…"
                    : platformName;
            platformList += "\n" + shortPlatformName;
        });

        return platformList;
    }
}

module.exports = DeleteAction;

DeleteAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "$teve.userSession",
    "$teve.entities.OdmOsPlatform",
];

DeleteAction.$displayDetails = {
    type: "Action",
    name: "DeleteAction",
    icon: "common.trash_bin",
    shown: true,
    title: "Delete IoT Platforms",
    order: 9,
};
